import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import SEO from "./SEO";
import axios from "axios";
import { Link } from "react-router-dom";

export default function Rooms() {
  const [activeRoom, setActiveRoom] = useState(null);
  const [roomAvailability, setRoomAvailability] = useState([]);

  useEffect(() => {
    axios
      .get("https://backend.ferejhotel.com/api/rooms")
      .then((res) => setRoomAvailability(res.data))
      .catch((err) => console.error("Error fetching rooms:", err));
  }, []);

  const rooms = [
    {
      id: 1,
      title: "Single Room",
      images: ["/sing1.jpg", "/sing2.jpg", "/sing3.jpg", "/sing4.jpg", "/sing5.jpg"],
      desc: "Comfortable single rooms ideal for solo travelers seeking peace and convenience.",
      details: [
        { en: "Single bed with cozy linens.", am: "አንድ መኝታ ከሚያስደስት ጨርቆች ጋር።" },
        { en: "Air conditioning and Wi-Fi.", am: "አየር መቆጣጠሪያ እና ኢንተርኔት።" },
        { en: "Private bathroom with hot water.", am: "የግል መታጠቢያ ክፍል ከሞቅ ውሃ ጋር።" },
        { en: "Smart TV.", am: "ስማርት ቲቪ።" },
      ],
    },
    {
      id: 2,
      title: "King Size Room",
      images: ["/King1.jpg", "/King2.jpg", "/King3.jpg", "/King4.jpg", "/King6.jpg"],
      desc: "Spacious and luxurious, offering maximum comfort with a king-sized bed.",
      details: [
        { en: "King-sized bed with premium linens.", am: "ከፍተኛ ጥራት ያላቸው መኝታ እና ጨርቆች።" },
        { en: "Private balcony and garden view.", am: "የአትክልት ቦታ እይታ ያለው ባልኮን።" },
        { en: "Smart TV and fast Wi-Fi.", am: "ስማርት ቲቪ እና ፈጣን ኢንተርኔት።" },
        { en: "Private bathroom with hot water.", am: "የግል መታጠቢያ ክፍል ከሞቅ ውሃ ጋር።" },
      ],
    },
    {
      id: 3,
      title: "Twin Room",
      images: ["/twin1.jpg", "/twin2.jpg", "/twin3.jpg", "/twin4.jpg"],
      desc: "Perfect for friends or family with two separate beds and cozy space.",
      details: [
        { en: "Two twin beds with comfort.", am: "ሁለት ተከፋፋይ መኝታዎች።" },
        { en: "Private bathroom with hot water.", am: "የግል መታጠቢያ ክፍል ከሞቅ ውሃ ጋር።" },
        { en: "Television and free Wi-Fi.", am: "ቲቪ እና ነፃ ኢንተርኔት።" },
      ],
    },
    {
      id: 4,
      title: "Suit (VIP) Room",
      images: ["/h1.jpg", "/h2.jpg", "/h3.jpg", "/h4.jpg", "/h5.jpg"],
      desc: "Luxurious suite with full amenities and private kitchen.",
      details: [
        { en: "Big size bed with comfort.", am: "ትሊክ የመኝታዎች መንገድ።" },
        { en: "Kitchen available to prepare meals.", am: "ምግብ ለመዘጋጀት የተዘጋጀ ኪችን።" },
        { en: "Private bathroom with hot water.", am: "የግል መታጠቢያ ክፍል ከሞቅ ውሃ ጋር።" },
        { en: "Television and free Wi-Fi.", am: "ቲቪ እና ነፃ ኢንተርኔት።" },
        { en: "You can cook by yourself.", am: "ራስዎ መብላት መዘጋጀት ይችላሉ።" },
        { en: "Refrigerator available.", am: "ፍሪጅ ዝግጁ ነው።" },
      ],
    },
  ];

  return (
    <section
      id="rooms"
      className="py-24 text-black font-[Poppins] overflow-hidden bg-[#fff8dc]"
    >
      <SEO
        title="Ferej Hotel - Harar, Ethiopia"
        description="Explore available rooms at Ferej Hotel Sol — Single, King Size, Twin, and VIP Suites."
        keywords="hotel in Harar, Ferej Hotel, Ferej Hotel Sol rooms, Harar Ethiopia hotels"
      />

      <div className="max-w-7xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ y: -50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-5xl font-bold text-yellow-700 mb-6 font-[Playfair_Display] drop-shadow-lg"
        >
          Available Rooms
        </motion.h2>

        <motion.p
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-xl text-gray-700 mb-14 max-w-3xl mx-auto leading-relaxed"
        >
          Discover our elegant selection of rooms designed for comfort,
          relaxation, and unforgettable experiences.
        </motion.p>

        {/* Room Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {rooms.map((room, index) => (
            <motion.div
              key={room.id}
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="relative bg-gradient-to-br from-[#fff6d1] via-[#ffd700] to-[#e6c200] text-black rounded-3xl shadow-2xl overflow-hidden border border-yellow-400/30 hover:shadow-yellow-400/50 hover:-translate-y-1 transition-all duration-500"
            >
              {/* ✅ Scrollable Image Section */}
              <div className="h-60 w-full overflow-x-auto scroll-smooth snap-x snap-mandatory flex gap-2 p-2 scrollbar-hide">
                {room.images.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${room.title}-${i}`}
                    className="h-56 w-80 object-cover rounded-xl snap-center flex-shrink-0"
                  />
                ))}
              </div>

              {/* Room Details */}
              <div className="p-8 relative z-10">
                <h3 className="text-2xl font-[Playfair_Display] font-bold text-yellow-800 mb-3 drop-shadow-sm">
                  {room.title}
                </h3>
                <p className="text-gray-800 mb-5 leading-relaxed text-[1.05rem]">
                  {room.desc}
                </p>
                <button
                  onClick={() =>
                    setActiveRoom(activeRoom === room.id ? null : room.id)
                  }
                  className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-black font-semibold px-6 py-2 rounded-full shadow-md hover:shadow-yellow-400/40 hover:scale-105 transition-all duration-300"
                >
                  {activeRoom === room.id ? "Hide Details" : "View Details"}
                </button>

                {activeRoom === room.id && (
                  <motion.ul
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mt-6 space-y-3 text-left text-gray-700 border-t border-yellow-400/30 pt-4 bg-yellow-100/40 backdrop-blur-sm rounded-xl p-4"
                  >
                    {room.details.map((d, i) => (
                      <li key={i} className="text-base leading-relaxed">
                        <span className="text-yellow-700">• {d.en}</span>
                        <br />
                        <span className="text-gray-500 italic text-sm">{d.am}</span>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Room Availability Section */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mt-20 bg-gradient-to-br from-[#fff6d1] via-[#ffd700] to-[#e6c200] text-black rounded-3xl p-10 shadow-2xl border border-yellow-400/20 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none"></div>

          <h3 className="text-3xl font-[Playfair_Display] text-yellow-800 mb-8 text-center drop-shadow-md">
            Room Availability
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-lg z-10 relative">
            {roomAvailability.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-[#fff6d1] via-[#ffd700] to-[#e6c200] backdrop-blur-lg p-6 rounded-2xl shadow-md border border-yellow-400/30 hover:shadow-yellow-400/50 hover:-translate-y-1 transition-all duration-300"
              >
                <p className="font-semibold text-xl text-yellow-800 mb-2 text-center">
                  {room.title}
                </p>
                <p className="text-yellow-900 font-medium text-center">
                  Available: {room.available}{" "}
                  {room.title === "Single Room"
                    ? "out of 6"
                    : room.title === "King Size Room"
                    ? "out of 23"
                    : room.title === "Twin Room"
                    ? "out of 3"
                    : room.title === "Suit (VIP) Room"
                    ? "out of 3"
                    : ""}
                </p>
                <div className="mt-3 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 transition-all duration-700"
                    style={{
                      width: `${
                        room.title === "Single Room"
                          ? (room.available / 6) * 100
                          : room.title === "King Size Room"
                          ? (room.available / 23) * 100
                          : room.title === "Twin Room"
                          ? (room.available / 3) * 100
                          : room.title === "Suit (VIP) Room"
                          ? (room.available / 3) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </motion.div>
            ))}
          </div>

         <div className="mt-10 flex flex-col sm:flex-row justify-center gap-6 z-10 relative">
  {/* Book Now - always visible */}
  <Link
    to="/booking"
    className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-black px-10 py-3 rounded-full font-semibold shadow-md hover:shadow-yellow-400/50 hover:scale-105 transition-all duration-300"
  >
    Book Now
  </Link>

  {/* Call - only visible on mobile (smaller than sm breakpoint) */}
  <a
    href="tel:+251919087201"
    className="bg-gray-900 text-yellow-400 px-10 py-3 rounded-full font-semibold shadow-md hover:bg-gray-800 hover:scale-105 transition-all duration-300 sm:hidden"
  >
    Call
  </a>
</div>

        </motion.div>
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Poppins:wght@400;500;600&display=swap');
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
    </section>
  );
}
